import { useContext, useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { FontIcon, Stack, Text } from '@fluentui/react'
import { useBoolean } from '@fluentui/react-hooks'
import DOMPurify from 'dompurify'
import remarkGfm from 'remark-gfm'
import supersub from 'remark-supersub'
import { AskResponse, Citation, DataPreviewTab, FilterEntry, lensFeedbackApi } from '../../api'
import { XSSAllowTags, XSSAllowAttributes } from '../../constants/sanatizeAllowables'
import { AppStateContext } from '../../state/AppProvider'
import { ThumbLikeRegular, ThumbDislikeRegular } from '@fluentui/react-icons'
import { CubeBadgeIcon } from '../common/CubeBadgeIcon'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded'
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded'
import { VisualsModal } from './VisualsModal'
import Popover from '@mui/material/Popover'
import { TableComponent } from '../../../components/table/NewTable'
import { GridColDef, GridSortModel, GridPaginationModel, GridFilterModel, GridCallbackDetails } from '@mui/x-data-grid-pro'

import { parseAnswer } from './AnswerParser'

import styles from './Answer.module.css'

interface Props {
  answer: AskResponse
  onCitationClicked: (citedDocument: Citation) => void
  onExectResultClicked: (answerId: string) => void
}

export const Answer = ({ answer, onCitationClicked, onExectResultClicked }: Props) => {
  const [isRefAccordionOpen, { toggle: toggleIsRefAccordionOpen }] = useBoolean(false)
  const filePathTruncationLimit = 50

  const parsedAnswer = useMemo(() => parseAnswer(answer), [answer])
  const [chevronIsExpanded, setChevronIsExpanded] = useState(isRefAccordionOpen)
  const [feedbackGiven, setFeedbackGiven] = useState<'like' | 'dislike' | null>(null)
  const [dataPreviewOpen, setDataPreviewOpen] = useState(false)
  const [visualsOpen, setVisualsOpen] = useState(false)

  // Anchor element for the filter badge popover
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null)
  const isFilterPopoverOpen = Boolean(filterAnchorEl)

  const handleFeedback = (type: 'like' | 'dislike') => {
    if (feedbackGiven) return
    setFeedbackGiven(type)
    const correlationId = answer.message_id
    if (correlationId) {
      lensFeedbackApi(correlationId, type === 'like')
    }
  }

  const appStateContext = useContext(AppStateContext)
  const SANITIZE_ANSWER = appStateContext?.state.frontendSettings?.sanitize_answer
  const hasTiming = typeof answer.generation_seconds === 'number'
  const intentLabel = answer.intent?.replace('-', ' ') ?? null

  const hasDataPreview = Array.isArray(answer.data_preview) && answer.data_preview.length > 0
  const hasAppliedFilters = !!answer.filters && Object.keys(answer.filters).length > 0

  // Tabbed data preview state
  const [activePreviewTab, setActivePreviewTab] = useState(0)
  const [sortModel, setSortModel] = useState<GridSortModel>([])
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 })
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] })

  const previewTabs: DataPreviewTab[] = hasDataPreview ? answer.data_preview! : []
  const activeTabData = previewTabs[activePreviewTab]

  // Reset grid state when switching tabs
  const handleTabChange = (index: number) => {
    setActivePreviewTab(index)
    setSortModel([])
    setPaginationModel({ page: 0, pageSize: 10 })
    setFilterModel({ items: [] })
  }

  const dataPreviewRows = useMemo(() => {
    if (!activeTabData?.preview?.length) return []
    return activeTabData.preview.map((row, idx) => ({ id: idx, ...row }))
  }, [activeTabData])

  const parseNumericPreviewValue = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isInteger(value) && Number.isFinite(value)) return value
    if (typeof value !== 'string') return null
    const text = value.trim()
    if (!text || !/^-?(?:\d{1,3}(?:,\d{3})+|\d+)$/.test(text)) return null
    const n = Number(text.replace(/,/g, ''))
    return Number.isFinite(n) ? n : null
  }

  const dataPreviewColumns: GridColDef[] = useMemo(() => {
    if (!activeTabData?.preview?.length) return []
    const rows = activeTabData.preview
    const keys = Object.keys(rows[0])
    const excludedNumericNameParts = ['code', 'cd', 'year', 'month', 'week', 'day', 'quarter']

    const numericColumns = new Set(
      keys.filter(key => {
        const lowerKey = key.toLowerCase()
        if (excludedNumericNameParts.some(part => lowerKey.includes(part))) return false
        const values = rows.map(row => row[key]).filter(v => v !== null && v !== undefined && v !== '')
        return values.length > 0 && values.every(v => parseNumericPreviewValue(v) !== null)
      })
    )

    return keys.map(key => {
      const base: GridColDef = { field: key, headerName: key, flex: 1, minWidth: 120 }
      if (!numericColumns.has(key)) return base
      return {
        ...base,
        type: 'number',
        valueGetter: (_value: unknown, row: Record<string, unknown>) => parseNumericPreviewValue(row[key]),
        valueFormatter: (value: unknown) =>
          typeof value === 'number' && Number.isFinite(value)
            ? value.toLocaleString('en-US', { maximumFractionDigits: 0 })
            : ''
      }
    })
  }, [activeTabData])

  const intentBadgeClass = (() => {
    switch (answer.intent) {
      case 'reasoning':    return styles.intentBadgeReasoning
      case 'single':       return styles.intentBadgeSingle
      case 'multi-query':  return styles.intentBadgeMultiQuery
      case 'refine':       return styles.intentBadgeRefine
      default:             return styles.intentBadgeSingle
    }
  })()

  const handleChevronClick = () => {
    setChevronIsExpanded(!chevronIsExpanded)
    toggleIsRefAccordionOpen()
  }

  useEffect(() => {
    setChevronIsExpanded(isRefAccordionOpen)
  }, [isRefAccordionOpen])

  const createCitationFilepath = (citation: Citation, index: number, truncate: boolean = false) => {
    let citationFilename = ''

    if (citation.filepath) {
      const part_i = citation.part_index ?? (citation.chunk_id ? parseInt(citation.chunk_id) + 1 : '')
      if (truncate && citation.filepath.length > filePathTruncationLimit) {
        const citationLength = citation.filepath.length
        citationFilename = `${citation.filepath.substring(0, 20)}...${citation.filepath.substring(citationLength - 20)} - Part ${part_i}`
      } else {
        citationFilename = `${citation.filepath} - Part ${part_i}`
      }
    } else if (citation.filepath && citation.reindex_id) {
      citationFilename = `${citation.filepath} - Part ${citation.reindex_id}`
    } else {
      citationFilename = `Citation ${index}`
    }
    return citationFilename
  }

  const components = {
    code({ node, ...props }: { node: any;[key: string]: any }) {
      let language
      if (props.className) {
        const match = props.className.match(/language-(\w+)/)
        language = match ? match[1] : undefined
      }
      const codeString = node.children[0].value ?? ''
      return (
        <SyntaxHighlighter style={nord} language={language} PreTag="div" {...props}>
          {codeString}
        </SyntaxHighlighter>
      )
    }
  }

  const LoadingAnswer = () => (
    <Stack className={styles.answerLoadingState} horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
      <span className={styles.answerLoadingLabel}>Generating insight</span>
      <span className={styles.loadingDots} aria-label="Loading">
        <span className={styles.loadingDot} />
        <span className={styles.loadingDot} />
        <span className={styles.loadingDot} />
      </span>
    </Stack>
  )

  return (
    <>
      <div className={styles.answerWrapper}>
      <Stack className={styles.answerContainer} tabIndex={0}>
        <Stack horizontal verticalAlign="center" horizontalAlign="space-between" className={styles.answerTopBar}>
          {!!answer.aas_catalog_name ? (
            <Stack horizontal verticalAlign="center" className={styles.answerTopBarLeft} tokens={{ childrenGap: 8 }}>
              {!!answer.aas_catalog_name && (
                <Stack horizontal verticalAlign="center" className={styles.cubeBadgeRow} tokens={{ childrenGap: 6 }}>
                  <CubeBadgeIcon
                    size={20}
                    className={styles.cubeBadgeIcon}
                    topClassName={styles.cubeFaceTop}
                    leftClassName={styles.cubeFaceLeft}
                    rightClassName={styles.cubeFaceRight}
                  />
                  <span className={styles.cubeBadgeText}>{answer.aas_catalog_name}</span>
                </Stack>
              )}
            </Stack>
          ) : (
            <span />
          )}
          <Stack horizontal verticalAlign="center" className={styles.answerTopBarRight} tokens={{ childrenGap: 8 }}>
            {hasAppliedFilters && (
              <>
                <button
                  className={styles.scopeIconBtn}
                  onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                  aria-label="Show applied scope"
                  title="Show applied scope"
                >
                  <FilterAltRoundedIcon sx={{ fontSize: 17 }} />
                </button>
                <Popover
                  open={isFilterPopoverOpen}
                  anchorEl={filterAnchorEl}
                  onClose={() => setFilterAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                  <div className={styles.filterPopoverContent}>
                    <div className={styles.filterPopoverPills}>
                      {Object.entries(answer.filters!).map(([key, entry]) => {
                        const [value, filterType] = entry as FilterEntry
                        const isRls = filterType === 'rls'
                        return (
                          <span key={key} className={`${styles.filterPopoverPill} ${isRls ? styles.filterPillRls : styles.filterPillQuery}`}>
                            {isRls
                              ? <PersonOutlineRoundedIcon sx={{ fontSize: 13, color: '#5b9bd5', marginRight: '3px', verticalAlign: 'text-bottom' }} />
                              : <FilterListRoundedIcon sx={{ fontSize: 13, color: '#e5813e', marginRight: '3px', verticalAlign: 'text-bottom' }} />
                            }
                            {String(key)}: {String(value)}
                          </span>
                        )
                      })}
                    </div>
                  </div>
                </Popover>
              </>
            )}
            {hasDataPreview && !answer.is_loading && (
              <button
                onClick={() => setVisualsOpen(true)}
                className={styles.dataPreviewBtn}
                aria-label="Open visuals"
              >
                <BarChartRoundedIcon sx={{ fontSize: 17 }} />
                <span>Visuals</span>
              </button>
            )}
            {hasDataPreview && !answer.is_loading && (
              <button
                onClick={() => setDataPreviewOpen(true)}
                className={styles.dataPreviewBtn}
                aria-label="Open data preview"
              >
                <OpenInNewRoundedIcon sx={{ fontSize: 17 }} />
                <span>Data Preview</span>
              </button>
            )}
          </Stack>
        </Stack>
        <Stack.Item>
          <Stack horizontal grow>
            <Stack.Item grow>
              {answer.is_loading ? (
                <LoadingAnswer />
              ) : (
                parsedAnswer && <ReactMarkdown
                  linkTarget="_blank"
                  remarkPlugins={[remarkGfm, supersub]}
                  children={
                    SANITIZE_ANSWER
                      ? DOMPurify.sanitize(parsedAnswer?.markdownFormatText, { ALLOWED_TAGS: XSSAllowTags, ALLOWED_ATTR: XSSAllowAttributes })
                      : parsedAnswer?.markdownFormatText
                  }
                  className={styles.answerText}
                  components={components}
                />
              )}
            </Stack.Item>
          </Stack>
        </Stack.Item>
        {parsedAnswer?.generated_chart !== null && (
          <Stack className={styles.answerContainer}>
            <Stack.Item grow>
              <img src={`data:image/png;base64, ${parsedAnswer?.generated_chart}`} />
            </Stack.Item>
          </Stack>
        )}
        {!answer.is_loading && <Stack horizontal className={styles.answerFooter}>
          {!!parsedAnswer?.citations.length && (
            <Stack.Item onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? toggleIsRefAccordionOpen() : null)}>
              <Stack style={{ width: '100%' }}>
                <Stack horizontal horizontalAlign="start" verticalAlign="center">
                  <Text
                    className={styles.accordionTitle}
                    onClick={toggleIsRefAccordionOpen}
                    aria-label="Open references"
                    tabIndex={0}
                    role="button">
                    <span>
                      {parsedAnswer.citations.length > 1
                        ? parsedAnswer.citations.length + ' references'
                        : '1 reference'}
                    </span>
                  </Text>
                  <FontIcon
                    className={styles.accordionIcon}
                    onClick={handleChevronClick}
                    iconName={chevronIsExpanded ? 'ChevronDown' : 'ChevronRight'}
                  />
                </Stack>
              </Stack>
            </Stack.Item>
          )}
          <Stack.Item className={styles.answerDisclaimerContainer}>
            <span className={styles.answerDisclaimer}>AI-generated content may be incorrect</span>
          </Stack.Item>
          <Stack.Item className={styles.answerBadgeGroup}>
            {intentLabel && (
              <span className={`${styles.answerIntentBadge} ${intentBadgeClass}`}>{intentLabel}</span>
            )}
            {hasTiming && (
              <span className={styles.answerTimingBadge}>{answer.generation_seconds?.toFixed(2)} s</span>
            )}
          </Stack.Item>
          {!!answer.exec_results?.length && (
            <Stack.Item onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? toggleIsRefAccordionOpen() : null)}>
              <Stack style={{ width: '100%' }}>
                <Stack horizontal horizontalAlign="start" verticalAlign="center">
                  <Text
                    className={styles.accordionTitle}
                    onClick={() => onExectResultClicked(answer.message_id ?? '')}
                    aria-label="Open Intents"
                    tabIndex={0}
                    role="button">
                    <span>
                      Show Intents
                    </span>
                  </Text>
                  <FontIcon
                    className={styles.accordionIcon}
                    onClick={handleChevronClick}
                    iconName={'ChevronRight'}
                  />
                </Stack>
              </Stack>
            </Stack.Item>
          )}
        </Stack>}
        {chevronIsExpanded && (
          <div className={styles.citationWrapper}>
            {parsedAnswer?.citations.map((citation, idx) => {
              return (
                <span
                  title={createCitationFilepath(citation, ++idx)}
                  tabIndex={0}
                  role="link"
                  key={idx}
                  onClick={() => onCitationClicked(citation)}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? onCitationClicked(citation) : null)}
                  className={styles.citationContainer}
                  aria-label={createCitationFilepath(citation, idx)}>
                  <div className={styles.citation}>{idx}</div>
                  {createCitationFilepath(citation, idx, true)}
                </span>
              )
            })}
          </div>
        )}
      </Stack>
      {hasDataPreview && (
        <Modal open={dataPreviewOpen} onClose={() => setDataPreviewOpen(false)}>
          <Box className={styles.dataPreviewModal}>
            <div className={styles.dataPreviewModalHeader}>
              <span className={styles.dataPreviewModalTitle}>Data Preview</span>
              <IconButton size="small" onClick={() => setDataPreviewOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
            {previewTabs.length > 1 && (
              <div className={styles.dataPreviewTabs}>
                {previewTabs.map((tab, idx) => (
                  <button
                    key={idx}
                    className={`${styles.dataPreviewTab} ${idx === activePreviewTab ? styles.dataPreviewTabActive : ''}`}
                    onClick={() => handleTabChange(idx)}
                  >
                    <span className={styles.dataPreviewTabLabel}>{tab.description}</span>
                    <span className={styles.dataPreviewTabSource}>
                      <CubeBadgeIcon size={12} />
                      {tab.source}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div className={styles.dataPreviewModalBody}>
              {/* Per-tab applied filters shown above the data grid */}
              {activeTabData?.filters && Object.keys(activeTabData.filters).length > 0 && (
                <div className={styles.dataPreviewFilterRow}>
                  <FilterAltRoundedIcon sx={{ fontSize: 14, color: '#757575' }} />
                  <span className={styles.dataPreviewFilterLabel}>Scope:</span>
                  {Object.entries(activeTabData.filters).map(([key, entry]) => {
                    const [value, filterType] = entry as FilterEntry
                    const isRls = filterType === 'rls'
                    return (
                      <span key={key} className={`${styles.dataPreviewFilterPill} ${isRls ? styles.filterPillRls : styles.filterPillQuery}`}>
                        {isRls
                          ? <PersonOutlineRoundedIcon sx={{ fontSize: 12, color: '#5b9bd5', marginRight: '3px', verticalAlign: 'text-bottom' }} />
                          : <FilterListRoundedIcon sx={{ fontSize: 12, color: '#e5813e', marginRight: '3px', verticalAlign: 'text-bottom' }} />
                        }
                        {String(key)}: {String(value)}
                      </span>
                    )
                  })}
                </div>
              )}
              <TableComponent
                columns={dataPreviewColumns}
                filteredData={dataPreviewRows}
                loading={false}
                sortModelData={sortModel}
                paginationModel={paginationModel}
                onPaginationModelChange={(model: GridPaginationModel, _details: GridCallbackDetails) => setPaginationModel(model)}
                onSortModelDataChange={(model: GridSortModel, _details: GridCallbackDetails) => setSortModel(model)}
                filterModel={filterModel}
                setFilterModel={setFilterModel}
                disableTableEdit
              />
            </div>
          </Box>
        </Modal>
      )}
      {hasDataPreview && (
        <VisualsModal
          open={visualsOpen}
          onClose={() => setVisualsOpen(false)}
          dataTabs={previewTabs}
        />
      )}
      {!answer.is_loading && (
        <div className={styles.feedbackButtonsContainer}>
          <ThumbLikeRegular
            className={`${styles.feedbackButton} ${feedbackGiven === 'like' ? styles.feedbackButtonActiveLike : feedbackGiven !== null ? styles.feedbackButtonDisabled : ''}`}
            onClick={() => handleFeedback('like')}
          />
          <ThumbDislikeRegular
            className={`${styles.feedbackButton} ${feedbackGiven === 'dislike' ? styles.feedbackButtonActiveDislike : feedbackGiven !== null ? styles.feedbackButtonDisabled : ''}`}
            onClick={() => handleFeedback('dislike')}
          />
        </div>
      )}
      </div>
    </>
  )
}
